import type { FC } from 'react'

import { AssetFallbackIcon, InfoBox } from '@skytech/ui'
import { ImageWithIconFallback } from '@skytech/ui/Image'
import AlertIcon from 'mdi-react/AlertIcon'
import TrashCanOutlineIcon from 'mdi-react/TrashCanOutlineIcon'
import { twJoin } from 'tailwind-merge'

import { useAppContext } from '@/App.context'
import type { GreenpoolAsset, PolicyCoreData } from '@/components/ecom-policy/types'
import { Translation } from '@/components/i18n/Translation'
import { statusStyleByWhiteLabel } from '@/components/util/style-constant'
import type { Asset } from '@/contexts/cart/cart-types'

import { getDifferenceInMonths } from '../ecom-policy/policy/helpers'
import { LocalDate } from '../LocalDate'

export const CartGreenpoolProduct: FC<{
  asset: Asset
  policy: PolicyCoreData
  onRemoveItem: (policyId: number, productId: number, force?: boolean) => void
}> = ({ asset, policy, onRemoveItem }) => {
  const { whiteLabel } = useAppContext()

  return (
    <div
      className={twJoin(
        'sm:shadow-basic flex max-w-full items-center justify-between gap-2 px-2 py-2 sm:gap-3 sm:rounded-lg sm:px-4',
        'max-sm:border-b max-sm:border-solid max-sm:border-transparent max-sm:border-b-gray-300/50 max-sm:last:border-b-transparent'
      )}
    >
      <div className="shadow-basic flex h-10 w-10 shrink-0 items-center justify-center overflow-hidden rounded-full">
        <ImageWithIconFallback
          src={asset.imgUrl || ''}
          alt="product"
          className="max-h-[80%]"
          fallbackIcon={<AssetFallbackIcon assetType={asset.assetType || 'OTHER'} size={1.5} />}
        />
      </div>

      <div className="flex w-3/5 shrink grow flex-col md:gap-0.5">
        <div className="text-sm text-gray-500 sm:text-base md:text-lg">
          {asset.assetName || <Translation id="Greenpool asset name" />}
        </div>
      </div>

      <TrashCanOutlineIcon
        className={twJoin(
          'size-6 shrink-0 cursor-pointer sm:size-7',
          statusStyleByWhiteLabel[whiteLabel]?.error.text ||
            statusStyleByWhiteLabel.skytech!.error.text
        )}
        onClick={() => onRemoveItem(policy.id, Number(asset.id))}
        onKeyDown={e => {
          if (e.key === 'Enter' || e.key === ' ') {
            onRemoveItem(policy.id, Number(asset.id), false)
          }
        }}
        role="button"
        tabIndex={0}
      />
    </div>
  )
}

export const LeaseMessage = ({
  selectedGreenpoolProduct,
}: {
  selectedGreenpoolProduct: GreenpoolAsset | undefined
}) => {
  const leaseDate = selectedGreenpoolProduct?.leaseReturnDate
  const leaseMonths = leaseDate
    ? getDifferenceInMonths({ endDate: new Date(leaseDate), startDate: new Date() })
    : ''
  if (!leaseDate) return null

  return (
    <InfoBox emphasis="warning" className="m-0 !mx-2 mt-4 text-sm sm:!mx-0 sm:text-base">
      <AlertIcon />
      <Translation
        id="This asset is leased and must be returned in {months} months, that is before {date}"
        months={leaseMonths}
        date={<LocalDate date={leaseDate || null} />}
      />
    </InfoBox>
  )
}
